import React, { useCallback } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import NavigationPrompt from 'react-router-navigation-prompt';
import RouteTabs from '../../../atomic/organism/RouteTabs';
import { NOTEBOOK } from '../../../constants/routes';
import { NotebookViews } from '../../../interfaces/notebook';
import { NOTEBOOK_NAVIGATION_MODAL_LABELS } from '../../../languages/en/notebook';
import {
  FeedsGridContainerLayout,
  FluidContainer,
  NoteBookContainer,
} from '../../../Utils/styles/display';
import NotebookDueDateViewController from '../NotebookDueDateController';
import {
  NotebookContainer,
  NotebookNavigationModal,
  TabContent,
} from './styles';
import useNotebookViewController from './useNotebookViewController';
import { notebookSections } from './useNotebookViewController/data';
import { NotebookTabData } from './useNotebookViewController/types';
import { NotebookBoardController } from '../NotebookBoardController';
import useMultiAssemblySlug from '../../../hooks/useMultiAssemblySlug';

const NotebookViewController = () => {
  const { slugUrl } = useMultiAssemblySlug();

  const {
    currentView,
    erroredTaskIDs,
    tabsValue,
    notebookTabs,
    hideNotebookNavigationModal,
    shouldShowModalForCurrentUser,
    handleViewChange,
    onDragEnd,
    markTasksAsRead,
  } = useNotebookViewController();

  const tabsComponent = useCallback(
    (data: NotebookTabData) => {
      switch (currentView) {
        case NotebookViews.Board:
          return (
            <TabContent value={tabsValue} key={data.value} index={data.index}>
              <FluidContainer>
                <NotebookBoardController markTasksAsRead={markTasksAsRead} />
              </FluidContainer>
            </TabContent>
          );
        case NotebookViews.Deadline:
          return (
            <TabContent value={tabsValue} key={data.value} index={data.index}>
              <FeedsGridContainerLayout>
                <NoteBookContainer style={{ width: 'unset' }}>
                  <DragDropContext onDragEnd={onDragEnd}>
                    {notebookSections.map((section) => {
                      return (
                        <NotebookDueDateViewController
                          viewType={NotebookViews.Deadline}
                          key={section}
                          type={section}
                          markTasksAsRead={markTasksAsRead}
                          shouldShowModalForCurrentUser={
                            shouldShowModalForCurrentUser
                          }
                        />
                      );
                    })}
                  </DragDropContext>
                </NoteBookContainer>
              </FeedsGridContainerLayout>
            </TabContent>
          );
        default:
          return <></>;
      }
    },
    [
      currentView,
      markTasksAsRead,
      onDragEnd,
      shouldShowModalForCurrentUser,
      tabsValue,
    ],
  );

  return (
    <FluidContainer>
      <RouteTabs
        routeTabButtons={notebookTabs}
        urlBase={`/${slugUrl}${NOTEBOOK}`}
        handleTabChange={handleViewChange}
        isFixed
      />
      <NotebookContainer>
        {notebookTabs.map((data: NotebookTabData) => tabsComponent(data))}
        <NavigationPrompt
          when={(currentLocation, nextLocation) =>
            !hideNotebookNavigationModal &&
            erroredTaskIDs.length > 0 &&
            location.pathname.includes('notebook') &&
            !nextLocation?.pathname.includes('notebook')
          }
          renderIfNotActive={false}
        >
          {({ onConfirm, onCancel }) => (
            <NotebookNavigationModal
              isOpen={!hideNotebookNavigationModal}
              onModalClose={onCancel}
              heading={NOTEBOOK_NAVIGATION_MODAL_LABELS.HEADING}
              primaryButton={{
                text: NOTEBOOK_NAVIGATION_MODAL_LABELS.PRIMARY_BUTTON.TEXT,
                onClick: onConfirm,
                status: 'warning',
              }}
              secondaryButton={{
                text: NOTEBOOK_NAVIGATION_MODAL_LABELS.SECONDARY_BUTTON.TEXT,
                onClick: onCancel,
              }}
            >
              {
                NOTEBOOK_NAVIGATION_MODAL_LABELS.NOTEBOOK_NAVIGATION_MODAL_PREFIX_TEXT
              }
            </NotebookNavigationModal>
          )}
        </NavigationPrompt>
      </NotebookContainer>
    </FluidContainer>
  );
};

export default NotebookViewController;
